var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from 'react';
import Modal from '../components/ui/modal';
const SCRIPT_ELEMENT_ID = 'stripe-js-script';
const FIELDS = ['cardNumber', 'cardExpiry', 'cardCvc', 'postalCode'];
const NO_COMPLETE = {
    'cardNumber': false,
    'cardExpiry': false,
    'cardCvc': false,
    'postalCode': false,
};
const NO_ERRORS = {
    'cardNumber': null,
    'cardExpiry': null,
    'cardCvc': null,
    'postalCode': null,
};
/** Stripe has no drop-in modal like Accept.js, so Elements is mounted inside our
 *  own modal. The card is split into its own number/expiry/cvc/postal Elements so
 *  it reads as a normal form rather than one combined strip. */
export const StripeCardForm = (props) => {
    const firstNameId = useId();
    const lastNameId = useId();
    const formId = useId();
    const fieldLabelId = useId();
    const firstNameRef = useRef(null);
    // Each Element renders its input inside an iframe, so the visible caption cannot
    // be a <label for>. The mount point is named through it instead.
    const labelId = (field) => `${fieldLabelId}-${field}`;
    const nodes = {
        'cardNumber': useRef(null),
        'cardExpiry': useRef(null),
        'cardCvc': useRef(null),
        'postalCode': useRef(null),
    };
    const numberRef = useRef(null);
    const stripeRef = useRef(null);
    const [scriptReady, setScriptReady] = useState(false);
    const [open, setOpen] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [complete, setComplete] = useState(NO_COMPLETE);
    const [errors, setErrors] = useState(NO_ERRORS);
    const [submitting, setSubmitting] = useState(false);
    const [failure, setFailure] = useState(null);
    useEffect(() => {
        const existing = document.getElementById(SCRIPT_ELEMENT_ID);
        const onLoad = () => setScriptReady(true);
        if (window.Stripe) {
            setScriptReady(true);
        }
        else if (existing) {
            existing.addEventListener('load', onLoad);
        }
        else {
            const script = document.createElement('script');
            script.id = SCRIPT_ELEMENT_ID;
            script.src = props.config.scriptUrl;
            script.async = true;
            script.addEventListener('load', onLoad);
            script.addEventListener('error', props.onLoadError);
            document.body.appendChild(script);
        }
        return () => {
            var _a;
            (_a = document.getElementById(SCRIPT_ELEMENT_ID)) === null || _a === void 0 ? void 0 : _a.removeEventListener('load', onLoad);
        };
    }, [props.config.scriptUrl]);
    // Mounted only while the modal is open: Stripe sizes each iframe on mount, and a
    // hidden container gives it nothing to measure. All four come from one elements()
    // group so passing the number Element to createPaymentMethod picks up the rest.
    useEffect(() => {
        const targets = FIELDS.map(x => nodes[x].current).filter(x => x !== null);
        if (open && scriptReady && window.Stripe && targets.length === FIELDS.length) {
            const stripe = window.Stripe(props.config.publicKey);
            const computed = getComputedStyle(targets[0]);
            const style = {
                'base': {
                    'color': computed.color,
                    'fontFamily': computed.fontFamily,
                    'fontSize': '16px',
                },
            };
            const elements = stripe.elements();
            const created = FIELDS.map((field, i) => {
                const element = elements.create(field, { 'style': style, 'showIcon': field === 'cardNumber' });
                element.on('change', (e) => {
                    setComplete(prev => (Object.assign(Object.assign({}, prev), { [field]: e.complete })));
                    setErrors(prev => { var _a; return (Object.assign(Object.assign({}, prev), { [field]: ((_a = e.error) === null || _a === void 0 ? void 0 : _a.message) || null })); });
                });
                element.mount(targets[i]);
                return element;
            });
            stripeRef.current = stripe;
            numberRef.current = created[0];
            return () => {
                created.forEach(x => x.destroy());
                stripeRef.current = null;
                numberRef.current = null;
            };
        }
    }, [open, scriptReady, props.config.publicKey]);
    useEffect(() => {
        var _a;
        if (open) {
            (_a = firstNameRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [open]);
    // Stripe takes a single billing name, so the two fields are joined on submit.
    const cardholder = `${firstName.trim()} ${lastName.trim()}`.trim();
    const cardComplete = FIELDS.every(x => complete[x]);
    const shownError = failure || FIELDS.map(x => errors[x]).find(x => !!x) || null;
    const close = () => {
        setOpen(false);
        setFirstName('');
        setLastName('');
        setComplete(NO_COMPLETE);
        setErrors(NO_ERRORS);
        setFailure(null);
    };
    const submit = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const stripe = stripeRef.current;
        const card = numberRef.current;
        if (stripe && card) {
            setSubmitting(true);
            setFailure(null);
            try {
                const result = yield stripe.createPaymentMethod({
                    'type': 'card',
                    'card': card,
                    'billing_details': { 'name': cardholder },
                });
                if ((_a = result.paymentMethod) === null || _a === void 0 ? void 0 : _a.id) {
                    const id = result.paymentMethod.id;
                    close();
                    yield props.onToken({ 'token': id, 'descriptor': '' });
                }
                else {
                    setFailure(((_b = result.error) === null || _b === void 0 ? void 0 : _b.message) || 'This card could not be saved.');
                }
            }
            catch (_c) {
                // Declines come back in result.error; a rejection here means the call never
                // completed, so say so rather than leaving the button spinning.
                setFailure('We could not reach the payment processor. Please try again.');
            }
            finally {
                setSubmitting(false);
            }
        }
    });
    return _jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: props.className, disabled: !!props.disabled || !scriptReady, onClick: () => setOpen(true), children: props.children }), _jsx(Modal, { isOpen: open, title: "Payment Information", onClose: close, footer: _jsx("button", { type: "submit", form: formId, className: "btn btn-primary", disabled: !cardComplete || !firstName.trim() || !lastName.trim() || submitting, children: submitting ? 'Saving…' : 'Add Card' }), children: _jsxs("form", { id: formId, onSubmit: (e) => { e.preventDefault(); void submit(); }, children: [_jsxs("div", { className: "row g-2 mb-3", children: [_jsxs("div", { className: "col-sm-6", children: [_jsx("label", { className: "form-label small fw-semibold", htmlFor: firstNameId, children: "First Name" }), _jsx("input", { id: firstNameId, ref: firstNameRef, type: "text", className: "form-control", autoComplete: "cc-given-name", maxLength: 100, value: firstName, onChange: (e) => setFirstName(e.target.value) })] }), _jsxs("div", { className: "col-sm-6", children: [_jsx("label", { className: "form-label small fw-semibold", htmlFor: lastNameId, children: "Last Name" }), _jsx("input", { id: lastNameId, type: "text", className: "form-control", autoComplete: "cc-family-name", maxLength: 100, value: lastName, onChange: (e) => setLastName(e.target.value) })] })] }), _jsxs("div", { className: "mb-3", children: [_jsx("div", { className: "form-label small fw-semibold", id: labelId('cardNumber'), children: "Card Number" }), _jsx("div", { className: "stripe-field", role: "group", "aria-labelledby": labelId('cardNumber'), ref: nodes.cardNumber })] }), _jsxs("div", { className: "row g-2", children: [_jsxs("div", { className: "col-4", children: [_jsx("div", { className: "form-label small fw-semibold", id: labelId('cardExpiry'), children: "Expiration" }), _jsx("div", { className: "stripe-field", role: "group", "aria-labelledby": labelId('cardExpiry'), ref: nodes.cardExpiry })] }), _jsxs("div", { className: "col-4", children: [_jsx("div", { className: "form-label small fw-semibold", id: labelId('cardCvc'), children: "CVC" }), _jsx("div", { className: "stripe-field", role: "group", "aria-labelledby": labelId('cardCvc'), ref: nodes.cardCvc })] }), _jsxs("div", { className: "col-4", children: [_jsx("div", { className: "form-label small fw-semibold", id: labelId('postalCode'), children: "ZIP" }), _jsx("div", { className: "stripe-field", role: "group", "aria-labelledby": labelId('postalCode'), ref: nodes.postalCode })] })] }), !!shownError && _jsx("div", { className: "text-danger small mt-3", role: "alert", children: shownError })] }) })] });
};
